import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Notification } from "../common/style";
import { Spinner, Table, Badge, Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, UncontrolledTooltip } from "reactstrap";
import Select from "react-select";
import { orderPanels, manageParts, manageHardwares } from "../dashboards/ops/service";

const ManageParts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.state?.orderId;
  const projectId = location.state?.projectId;
  const customerName = location.state?.customerName;
  const projectName = location.state?.projectName;
  const orderIdentifier = location.state?.orderIdentifier;
  const isSalesUser = location.state?.is_sales_user;
  const [isLoading, setIsLoading] = useState(false);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [partsData, setPartsData] = useState(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState(null); // null means "All Panels"
  const [selectedMaterial, setSelectedMaterial] = useState(null); // null means "All Materials"
  const [selectedPartIds, setSelectedPartIds] = useState(new Set()); // Track selected part IDs (persists across filter changes)
  const [selectedHardwareIds, setSelectedHardwareIds] = useState(new Set()); // Track selected hardware IDs
  const [isMovingParts, setIsMovingParts] = useState(false);
  const [isMovingHardwares, setIsMovingHardwares] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("panels"); // "panels" or "hardwares"
  const [sortColumn, setSortColumn] = useState(null); // Column name to sort by
  const [sortAsc, setSortAsc] = useState(false); // Sort direction (false = descending, true = ascending)

  useEffect(() => {
    if (orderId) {
      // Fetch order panels
      setIsLoading(true);
      orderPanels({ orderId })
        .then((response) => {
      setPartsData(response);
      setSelectedShipmentId(null); // Reset to "All Panels" when new data is loaded
      setSelectedPartIds(new Set()); // Clear selected parts when new data is loaded
      setSelectedHardwareIds(new Set()); // Clear selected hardwares when new data is loaded
      setSortColumn(null); // Reset sort when new data is loaded
      setSortAsc(false);
      setIsNotificationVisible(false);
        })
        .catch((error) => {
          setIsNotificationVisible(true);
          setNotification({
            type: "error",
            message: error.message || error.error || "Failed to fetch parts data",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [orderId]);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  // Handle row selection - track by part ID to persist across filter changes
  const handleRowSelect = (rowIndex) => {
    const row = tableData.rows[rowIndex];
    // Prevent selection of rows from completed shipments
    if (row && row._isCompleted) {
      return;
    }
    // Track by part ID instead of row index
    if (row && row._partId !== null && row._partId !== undefined) {
      setSelectedPartIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(row._partId)) {
          newSet.delete(row._partId);
        } else {
          newSet.add(row._partId);
        }
        return newSet;
      });
    }
  };

  // Key mapping from snake_case to display names
  const keyToDisplayName = {
    material: 'Material',
    board_name: 'Board Name',
    fb_name: 'FB Name',
    thickness: 'Thickness',
    fb_length: 'FB Length',
    fb_width: 'FB Width',
    quantity: 'Quantity',
    texture_direction: 'Texture Direction',
    left_edge_band_amount: 'Left Edge Band',
    right_edge_band_amount: 'Right Edge Band',
    top_edge_band_amount: 'Top Edge Band',
    bottom_edge_band_amount: 'Bottom Edge Band',
    cabinet_name: 'Cabinet Name',
    section_name: 'Section Name',
    customer: 'Customer',
    remark: 'Remark',
    room_name: 'Room Name',
    assembly_label: 'Assembly Label',
    naadi_format: 'Naadi Format',
    shipment_id: 'Shipment ID',
    shipment_name: 'Shipment Name',
    shipment_status: 'Shipment Status',
    part_id: 'Part ID'
  };

  // Helper function to parse comma-separated values
  const parseCommaSeparated = (value) => {
    if (!value) return [];
    if (typeof value === 'string') {
      return value.split(',').map(v => v.trim()).filter(v => v);
    }
    return [value];
  };

  // Extract unique shipments from the data
  const shipments = useMemo(() => {
    if (!partsData || typeof partsData !== 'object') return [];

    // Check if new format (panels array) or old format (column arrays)
    const panels = partsData.panels;
    if (Array.isArray(panels)) {
      // New format: panels array
      const shipmentMap = new Map();

      panels.forEach(panel => {
        const shipmentIds = parseCommaSeparated(panel.shipment_id);
        const shipmentNames = parseCommaSeparated(panel.shipment_name);
        const shipmentStatuses = parseCommaSeparated(panel.shipment_status);

        shipmentIds.forEach((id, idx) => {
          const shipmentId = String(id);
            const name = shipmentNames[idx] || '';
            const status = shipmentStatuses[idx] || '';
          const isCompleted = String(status).toLowerCase() === 'completed';

          if (shipmentId && name) {
            if (!shipmentMap.has(shipmentId)) {
              shipmentMap.set(shipmentId, {
                name: name,
                status: status,
                isCompleted: isCompleted
              });
            } else {
              // Update if this one is completed
              const existing = shipmentMap.get(shipmentId);
              if (isCompleted) {
                shipmentMap.set(shipmentId, {
                  name: existing.name,
                  status: status,
                  isCompleted: true
                });
              }
            }
          }
        });
      });

      return Array.from(shipmentMap.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        status: data.status,
        isCompleted: data.isCompleted
      }));
    } else {
      // Old format: column arrays (backward compatibility)
      const shipmentIds = partsData['Shipment ID'];
      const shipmentNames = partsData['Shipment Name'];

      if (!Array.isArray(shipmentIds) || !Array.isArray(shipmentNames)) return [];

      const shipmentStatusField = ['Shipment Status', 'ShipmentStatus', 'Status', 'status'].find(
        field => partsData[field] && Array.isArray(partsData[field])
      );
      const shipmentStatuses = shipmentStatusField ? partsData[shipmentStatusField] : null;

      const shipmentMap = new Map();
      shipmentIds.forEach((id, index) => {
        if (id && shipmentNames[index]) {
          const shipmentId = String(id);
          const status = shipmentStatuses && shipmentStatuses[index]
            ? String(shipmentStatuses[index]).toLowerCase()
            : null;
          const isCompleted = status === 'completed';

          if (!shipmentMap.has(shipmentId)) {
            shipmentMap.set(shipmentId, {
              name: shipmentNames[index],
              status: status,
              isCompleted: isCompleted
            });
          } else {
            const existing = shipmentMap.get(shipmentId);
            if (isCompleted) {
              shipmentMap.set(shipmentId, {
                name: existing.name,
                status: status,
                isCompleted: true
              });
            }
          }
        }
      });

      return Array.from(shipmentMap.entries()).map(([id, data]) => ({
        id,
        name: data.name,
        status: data.status,
        isCompleted: data.isCompleted
      }));
    }
  }, [partsData]);

  // Extract unique materials from the data
  const materials = useMemo(() => {
    if (!partsData || typeof partsData !== 'object') return [];

    // Check if new format (panels array) or old format (column arrays)
    const panels = partsData.panels;
    if (Array.isArray(panels)) {
      // New format: extract from panels array
      const uniqueMaterials = [...new Set(panels.map(p => p.material).filter(m => m != null && m !== ''))];
      return uniqueMaterials.sort();
    } else {
      // Old format: column arrays (backward compatibility)
      const materialColumn = partsData['Material'];
      if (!Array.isArray(materialColumn)) return [];
      const uniqueMaterials = [...new Set(materialColumn.filter(m => m != null && m !== ''))];
      return uniqueMaterials.sort();
    }
  }, [partsData]);

  // Convert the response data into table rows
  const tableData = useMemo(() => {
    if (!partsData || typeof partsData !== 'object') return { columns: [], rows: [] };

    // Check if new format (panels array) or old format (column arrays)
    const panels = partsData.panels;

    if (Array.isArray(panels)) {
      // New format: panels array
      let filteredPanels = [...panels];

      // Filter by selected shipment ID if one is selected
      if (selectedShipmentId !== null) {
        filteredPanels = filteredPanels.filter(panel => {
          const shipmentIds = parseCommaSeparated(panel.shipment_id);
          return shipmentIds.some(id => String(id) === String(selectedShipmentId));
        });
      }

      // Filter by selected material if one is selected
      if (selectedMaterial !== null) {
        filteredPanels = filteredPanels.filter(panel => panel.material === selectedMaterial);
      }

      if (filteredPanels.length === 0) return { columns: [], rows: [] };

      // Get all keys from first panel to determine columns
      const firstPanel = filteredPanels[0];
      const allKeys = Object.keys(firstPanel);

      // Exclude internal fields and map to display names
      const excludedKeys = ['shipment_id', 'shipment_name', 'shipment_status', 'part_id', 'naadi_format'];
      const displayColumns = allKeys
        .filter(key => !excludedKeys.includes(key))
        .map(key => keyToDisplayName[key] || key)
        .sort((a, b) => {
          // Custom sort order for important columns
          const order = ['Material', 'FB Name', 'Thickness', 'Room Name', 'Cabinet Name', 'Section Name'];
          const aIndex = order.indexOf(a);
          const bIndex = order.indexOf(b);
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          return a.localeCompare(b);
        });

      // Create rows
      const rows = filteredPanels.map((panel, index) => {
        // Get shipment status - check if any shipment is completed
        const shipmentStatuses = parseCommaSeparated(panel.shipment_status);
        const isCompleted = shipmentStatuses.some(status => String(status).toLowerCase() === 'completed');

        // Get part IDs - can be comma-separated
        const partIds = parseCommaSeparated(panel.part_id);
        const primaryPartId = partIds.length > 0 ? partIds[0] : null;

        const row = {
          _originalIndex: index,
          _partId: primaryPartId,
          _isCompleted: isCompleted,
        };

        // Map panel data to display columns
        allKeys.forEach(key => {
          if (!excludedKeys.includes(key)) {
            const displayName = keyToDisplayName[key] || key;
            row[displayName] = panel[key] !== null && panel[key] !== undefined ? panel[key] : null;
          }
        });

        return row;
      });

      // Apply sorting if a sort column is selected
      const sortableColumns = ['Material', 'FB Name', 'Thickness', 'Room Name'];
      if (sortColumn && sortableColumns.includes(sortColumn)) {
        rows.sort((a, b) => {
          let aVal = a[sortColumn];
          let bVal = b[sortColumn];

          if (aVal === null || aVal === undefined) aVal = '';
          if (bVal === null || bVal === undefined) bVal = '';

          aVal = String(aVal);
          bVal = String(bVal);

          if (sortColumn === 'Thickness') {
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return sortAsc ? aNum - bNum : bNum - aNum;
            }
          }

          const comparison = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
          return sortAsc ? comparison : -comparison;
        });
      }

      return { columns: displayColumns, rows };
    } else {
      // Old format: column arrays (backward compatibility)
      const allColumns = Object.keys(partsData);
      const excludedColumns = ['Naadi Format', 'Shipment ID', 'Shipment Name', 'Shipment Status', 'ShipmentStatus', 'Part ID', 'PartID', 'Panel ID', 'PanelID'];
      const columns = allColumns.filter(col => !excludedColumns.includes(col));

      if (columns.length === 0) return { columns: [], rows: [] };

      const shipmentIds = partsData['Shipment ID'];
      const isShipmentIdArray = Array.isArray(shipmentIds);

      const shipmentStatusField = ['Shipment Status', 'ShipmentStatus', 'Status', 'status'].find(
        field => partsData[field] && Array.isArray(partsData[field])
      );
      const shipmentStatuses = shipmentStatusField ? partsData[shipmentStatusField] : null;

      const maxLength = Math.max(...columns.map(col => {
        const value = partsData[col];
        return Array.isArray(value) ? value.length : 0;
      }));

      const partIdField = ['ID', 'Part ID', 'Panel ID', 'id', 'part_id', 'panel_id'].find(
        field => partsData[field] && Array.isArray(partsData[field])
      );
      const partIds = partIdField ? partsData[partIdField] : null;

      const rows = [];
      for (let i = 0; i < maxLength; i++) {
        if (selectedShipmentId !== null && isShipmentIdArray) {
          const rowShipmentId = shipmentIds[i];
          if (String(rowShipmentId) !== String(selectedShipmentId)) {
            continue;
          }
        }

        if (selectedMaterial !== null) {
          const materialColumn = partsData['Material'];
          if (Array.isArray(materialColumn) && materialColumn[i] !== selectedMaterial) {
            continue;
          }
        }

        const rowShipmentStatus = shipmentStatuses && shipmentStatuses[i]
          ? String(shipmentStatuses[i]).toLowerCase()
          : null;
        const isCompleted = rowShipmentStatus === 'completed';

        const row = {
          _originalIndex: i,
          _partId: partIds && partIds[i] ? partIds[i] : null,
          _isCompleted: isCompleted,
        };
        columns.forEach(col => {
          const value = partsData[col];
          row[col] = Array.isArray(value) ? (value[i] !== undefined ? value[i] : null) : value;
        });
        rows.push(row);
      }

      const sortableColumns = ['Material', 'FB Name', 'Thickness', 'Room Name'];
      if (sortColumn && sortableColumns.includes(sortColumn)) {
        rows.sort((a, b) => {
          let aVal = a[sortColumn];
          let bVal = b[sortColumn];

          if (aVal === null || aVal === undefined) aVal = '';
          if (bVal === null || bVal === undefined) bVal = '';

          aVal = String(aVal);
          bVal = String(bVal);

          if (sortColumn === 'Thickness') {
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return sortAsc ? aNum - bNum : bNum - aNum;
            }
          }

          const comparison = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
          return sortAsc ? comparison : -comparison;
        });
      }

      return { columns, rows };
    }
  }, [partsData, selectedShipmentId, selectedMaterial, sortColumn, sortAsc]);

  // Handle select all - track by part ID to persist across filter changes
  const handleSelectAll = (checked) => {
    if (checked) {
      // Only select rows that are not from completed shipments
      const selectablePartIds = tableData.rows
        .filter(row => !row._isCompleted && row._partId !== null && row._partId !== undefined)
        .map(row => row._partId);
      setSelectedPartIds(prev => {
        const newSet = new Set(prev);
        selectablePartIds.forEach(partId => newSet.add(partId));
        return newSet;
      });
    } else {
      // Only deselect currently visible rows
      const visiblePartIds = tableData.rows
        .filter(row => row._partId !== null && row._partId !== undefined)
        .map(row => row._partId);
      setSelectedPartIds(prev => {
        const newSet = new Set(prev);
        visiblePartIds.forEach(partId => newSet.delete(partId));
        return newSet;
      });
    }
  };

  // Calculate if all selectable rows are selected (excluding completed shipments)
  const selectableRows = tableData.rows.filter(row => !row._isCompleted && row._partId !== null && row._partId !== undefined);
  const visibleSelectedCount = selectableRows.filter(row => selectedPartIds.has(row._partId)).length;
  const isAllSelected = selectableRows.length > 0 && visibleSelectedCount === selectableRows.length;
  const hasOnlyCompletedRows = tableData.rows.length > 0 && selectableRows.length === 0;

  // Handle column sorting
  const handleSort = (columnName) => {
    if (sortColumn === columnName) {
      // Toggle sort direction if clicking the same column
      setSortAsc(!sortAsc);
    } else {
      // Set new sort column and default to descending
      setSortColumn(columnName);
      setSortAsc(false);
    }
  };

  // Render sort icon for column headers
  const renderSortIcon = (columnName) => {
    // Always show arrow for sortable columns
    if (sortColumn === columnName) {
      // Show active sort direction
      return sortAsc ? (
        <i className="fa-solid fa-arrow-up ms-1" style={{ fontSize: "0.7rem" }}></i>
      ) : (
        <i className="fa-solid fa-arrow-down ms-1" style={{ fontSize: "0.7rem" }}></i>
      );
    } else {
      // Show default down arrow for unsorted sortable columns
      return <i className="fa-solid fa-arrow-down ms-1" style={{ fontSize: "0.7rem", opacity: 0.5 }}></i>;
    }
  };

  // Process hardwares data from API response
  const hardwaresData = useMemo(() => {
    if (!partsData || typeof partsData !== 'object') return [];

    // Check if new format (hardwares array) or old format
    const hardwares = partsData.hardwares;
    if (!Array.isArray(hardwares)) return [];

    let filteredHardwares = [...hardwares];

    // Filter by selected shipment ID if one is selected
    if (selectedShipmentId !== null) {
      filteredHardwares = filteredHardwares.filter(hw => {
        return hw.shipment_id && String(hw.shipment_id) === String(selectedShipmentId);
      });
    }

    return filteredHardwares.map(hw => ({
      id: hw.id,
      material_name: hw.material_name,
      material_sku: hw.material_sku,
      quantity: hw.quantity,
      order_item_name: hw.order_item_name,
      room_name: hw.room_name,
      section_name: hw.section_name,
      shipment_id: hw.shipment_id,
      shipment_name: hw.shipment_name,
      shipment_status: hw.shipment_status,
      _isCompleted: String(hw.shipment_status || '').toLowerCase() === 'completed'
    }));
  }, [partsData, selectedShipmentId]);

  // Handle hardware row selection
  const handleHardwareRowSelect = (hardwareId) => {
    setSelectedHardwareIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(hardwareId)) {
        newSet.delete(hardwareId);
      } else {
        newSet.add(hardwareId);
      }
      return newSet;
    });
  };

  // Handle select all hardwares
  const handleSelectAllHardwares = (checked) => {
    if (checked) {
      const selectableHardwareIds = hardwaresData
        .filter(hw => !hw._isCompleted && hw.id)
        .map(hw => hw.id);
      setSelectedHardwareIds(prev => {
        const newSet = new Set(prev);
        selectableHardwareIds.forEach(id => newSet.add(id));
        return newSet;
      });
    } else {
      const visibleHardwareIds = hardwaresData
        .filter(hw => hw.id)
        .map(hw => hw.id);
      setSelectedHardwareIds(prev => {
        const newSet = new Set(prev);
        visibleHardwareIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  const selectableHardwares = hardwaresData.filter(hw => !hw._isCompleted && hw.id);
  const visibleSelectedHardwaresCount = selectableHardwares.filter(hw => selectedHardwareIds.has(hw.id)).length;
  const isAllHardwaresSelected = selectableHardwares.length > 0 && visibleSelectedHardwaresCount === selectableHardwares.length;
  const hasOnlyCompletedHardwares = hardwaresData.length > 0 && selectableHardwares.length === 0;

  // Handle moving parts to a shipment
  // This function supports moving parts across different shipments
  // Selected parts can come from multiple shipments and will all be moved to the target shipment
  const handleMoveParts = async (targetShipmentId) => {
    if (!isSalesUser) return;
    if (!window.confirm("Are you sure you want to move the selected parts to the target shipment?")) {
      return;
    }

    if (selectedPartIds.size === 0) {
      setIsNotificationVisible(true);
      setNotification({
        type: "error",
        message: "Please select at least one panel to move",
      });
      return;
    }

    // Convert Set to Array for the API call
    const partIdsArray = Array.from(selectedPartIds).filter(id => id !== null && id !== undefined);

    if (partIdsArray.length === 0) {
      setIsNotificationVisible(true);
      setNotification({
        type: "error",
        message: "Could not find part IDs for selected panels",
      });
      return;
    }

    setIsMovingParts(true);
    try {
      await manageParts({
        orderId,
        partIds: partIdsArray,
        shipmentId: targetShipmentId || null,
      });

      // Refresh the data after successful move
      const response = await orderPanels({ orderId });
      setPartsData(response);
      setSelectedPartIds(new Set()); // Clear selection after move

      setIsNotificationVisible(true);
      setNotification({
        type: "success",
        message: `Successfully moved ${partIdsArray.length} panel(s)`,
      });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotification({
        type: "error",
        message: error.message || error.error || "Failed to move parts",
      });
    } finally {
      setIsMovingParts(false);
    }
  };

  // Handle moving hardwares to a shipment
  const handleMoveHardwares = async (targetShipmentId) => {
    if (!isSalesUser) return;
    if (!window.confirm("Are you sure you want to move the selected hardwares to the target shipment?")) {
      return;
    }

    if (selectedHardwareIds.size === 0) {
      setIsNotificationVisible(true);
      setNotification({
        type: "error",
        message: "Please select at least one hardware to move",
      });
      return;
    }

    // Convert Set to Array for the API call
    const hardwareIdsArray = Array.from(selectedHardwareIds).filter(id => id !== null && id !== undefined);

    if (hardwareIdsArray.length === 0) {
      setIsNotificationVisible(true);
      setNotification({
        type: "error",
        message: "Could not find hardware IDs for selected hardwares",
      });
      return;
    }

    setIsMovingHardwares(true);
    try {
      await manageHardwares({
        orderId,
        hardwareIds: hardwareIdsArray,
        shipmentId: targetShipmentId || null,
      });

      // Refresh the data after successful move
      const response = await orderPanels({ orderId });
      setPartsData(response);
      setSelectedHardwareIds(new Set()); // Clear selection after move

      setIsNotificationVisible(true);
      setNotification({
        type: "success",
        message: `Successfully moved ${hardwareIdsArray.length} hardware(s)`,
      });
    } catch (error) {
      setIsNotificationVisible(true);
      setNotification({
        type: "error",
        message: error.message || error.error || "Failed to move hardwares",
      });
    } finally {
      setIsMovingHardwares(false);
    }
  };

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      {orderId ? (
        <div className="my-3">
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3 px-3">
              <h5 className="fw-bold mb-0">
                {customerName && projectName ? (
                  <>
                    {customerName}
                    {" -> "}
                    {projectName}
                    {orderIdentifier && <span className="ms-2 text-muted small">#{orderIdentifier}</span>}
                  </>
                ) : "Manage Parts"}
              </h5>
              <i
                className="fa-solid fa-times fs-5 pointer"
                onClick={() => navigate(-1)}
                style={{ cursor: "pointer" }}
                title="Close"
              />
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center align-items-center p-5">
                <Spinner />
              </div>
            ) : (
              <div className="p-3 border rounded">
                {/* Filters and Move Parts Button */}
                {shipments.length > 0 && (
                  <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                    {/* Filters on the left */}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {/* Shipment Tags */}
                      {shipments.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <Badge
                            color={selectedShipmentId === null ? "primary" : "secondary"}
                            style={{ cursor: "pointer", fontSize: "0.9rem", padding: "0.5rem 1rem" }}
                            onClick={() => {
                              setSelectedShipmentId(null);
                              // Don't clear selection - keep selected parts across filter changes
                            }}
                          >
                            All
                          </Badge>
                          {shipments.map((shipment) => (
                            <Badge
                              key={shipment.id}
                              color={selectedShipmentId === shipment.id ? "primary" : "secondary"}
                              style={{ cursor: "pointer", fontSize: "0.9rem", padding: "0.5rem 1rem" }}
                              onClick={() => {
                                setSelectedShipmentId(shipment.id);
                                // Don't clear selection - keep selected parts across filter changes
                              }}
                            >
                              {shipment.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Move Parts/Hardwares Button on the right */}
                    <UncontrolledDropdown>
                      <DropdownToggle
                        caret
                        color="secondary"
                        className="fw-bold rounded-1"
                        style={{
                          minWidth: "150px",
                          backgroundColor: "#6c757d",
                          borderColor: "#6c757d"
                        }}
                        disabled={
                          !isSalesUser ||
                          (activeSubTab === "panels"
                            ? (isMovingParts || selectedPartIds.size === 0)
                            : (isMovingHardwares || selectedHardwareIds.size === 0))
                        }
                      >
                        {activeSubTab === "panels" ? (
                          isMovingParts ? "Moving..." : `Move Parts (${selectedPartIds.size})`
                        ) : (
                          isMovingHardwares ? "Moving..." : `Move Hardwares (${selectedHardwareIds.size})`
                        )}
                      </DropdownToggle>
                      <DropdownMenu
                        style={{
                          backgroundColor: "#f7f2f2",
                          fontSize: "12px",
                        }}
                      >
                        {shipments.map((shipment) => {
                          const isDisabled = !isSalesUser || (activeSubTab === "panels"
                            ? (isMovingParts || selectedPartIds.size === 0 || shipment.isCompleted)
                            : (isMovingHardwares || selectedHardwareIds.size === 0 || shipment.isCompleted));
                          const tooltipId = `shipment-tooltip-${shipment.id}`;
                          return (
                            <React.Fragment key={shipment.id}>
                              <DropdownItem
                                id={tooltipId}
                                onClick={(e) => {
                                  if (isDisabled) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  if (activeSubTab === "panels") {
                                    handleMoveParts(shipment.id);
                                  } else {
                                    handleMoveHardwares(shipment.id);
                                  }
                                }}
                                style={{
                                  cursor: isDisabled ? "not-allowed" : "pointer",
                                  opacity: shipment.isCompleted ? 0.5 : 1,
                                }}
                              >
                                {shipment.name}
                              </DropdownItem>
                              {shipment.isCompleted && (
                                <UncontrolledTooltip target={tooltipId} placement="right">
                                  Can't move {activeSubTab === "panels" ? "parts" : "hardwares"} to delivered shipments.
                                </UncontrolledTooltip>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                )}
                {/* Panels | Hardwares Sub-tabs */}
                <div className="mb-3 d-flex align-items-center gap-2 border-bottom pb-2">
                  <Badge
                    color={activeSubTab === "panels" ? "primary" : "secondary"}
                    style={{ cursor: "pointer", fontSize: "0.9rem", padding: "0.5rem 1rem" }}
                    onClick={() => setActiveSubTab("panels")}
                  >
                    Panels
                  </Badge>
                  <Badge
                    color={activeSubTab === "hardwares" ? "primary" : "secondary"}
                    style={{ cursor: "pointer", fontSize: "0.9rem", padding: "0.5rem 1rem" }}
                    onClick={() => setActiveSubTab("hardwares")}
                  >
                    Hardwares
                  </Badge>
                </div>
                {/* Material Filter - Show only for Panels tab */}
                {activeSubTab === "panels" && materials.length > 0 && (
                  <div className="mb-3" style={{ minWidth: "200px", zIndex: 1000 }}>
                    <Select
                      options={[
                        { label: "All Materials", value: null },
                        ...materials.map(m => ({ label: m, value: m }))
                      ]}
                      value={selectedMaterial ? { label: selectedMaterial, value: selectedMaterial } : { label: "All Materials", value: null }}
                      onChange={(option) => setSelectedMaterial(option ? option.value : null)}
                      placeholder="Filter by Material"
                      isClearable={false}
                      styles={{
                        menu: (provided) => ({ ...provided, zIndex: 1000 }),
                        menuPortal: (provided) => ({ ...provided, zIndex: 1000 })
                      }}
                      menuPortalTarget={document.body}
                    />
                  </div>
                )}
                {activeSubTab === "panels" && tableData.rows.length > 0 ? (
                  <div className="mt-3 table-responsive" style={{ maxHeight: "85vh", overflowY: "auto" }}>
                    <Table bordered hover size="sm" className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                      <thead className="bg-secondary text-white" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        <tr>
                          <th scope="col" className="bg-secondary text-white text-center" style={{ width: "50px" }}>
                            <span id="select-all-checkbox" style={{ display: "inline-block" }}>
                              <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectAll(e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={hasOnlyCompletedRows}
                                className="form-check-input"
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  cursor: hasOnlyCompletedRows ? "not-allowed" : "pointer",
                                }}
                              />
                              {hasOnlyCompletedRows && (
                                <UncontrolledTooltip target="select-all-checkbox" placement="bottom">
                                  Can’t move parts from delivered shipments.
                                </UncontrolledTooltip>
                              )}
                            </span>
                          </th>
                          {tableData.columns.map((column, index) => {
                            const sortableColumns = ['Material', 'FB Name', 'Thickness', 'Room Name'];
                            const isSortable = sortableColumns.includes(column);
                            return (
                              <th
                                key={index}
                                scope="col"
                                className={`bg-secondary text-white ${isSortable ? 'pointer' : ''}`}
                                onClick={isSortable ? () => handleSort(column) : undefined}
                                style={isSortable ? { cursor: 'pointer' } : {}}
                              >
                                {column} {isSortable && renderSortIcon(column)}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.rows.map((row, rowIndex) => {
                          const isCompleted = row._isCompleted;
                          const isSelected = row._partId !== null && row._partId !== undefined && selectedPartIds.has(row._partId);
                          const checkboxId = `checkbox-${rowIndex}-${row._partId}`;
                          const rowId = `row-${rowIndex}-${row._partId}`;
                          return (
                          <tr
                            key={rowIndex}
                            id={rowId}
                            onClick={() => !isCompleted && handleRowSelect(rowIndex)}
                            style={{
                              backgroundColor: isSelected ? '#d0e7ff' : undefined,
                              cursor: isCompleted ? "not-allowed" : "pointer",
                              opacity: isCompleted ? 0.6 : 1
                            }}
                          >
                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                              <span id={checkboxId} style={{ display: "inline-block" }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => !isCompleted && handleRowSelect(rowIndex)}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={isCompleted}
                                  className="form-check-input"
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: isCompleted ? "not-allowed" : "pointer"
                                  }}
                                />
                                {isCompleted && (
                                  <UncontrolledTooltip target={checkboxId} placement="bottom">
                                    Can’t move parts from delivered shipments.
                                  </UncontrolledTooltip>
                                )}
                              </span>
                            </td>
                            {tableData.columns.map((column, colIndex) => (
                              <td key={colIndex}>
                                {row[column] !== null && row[column] !== undefined
                                  ? String(row[column])
                                  : "-"}
                              </td>
                            ))}
                          </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                ) : activeSubTab === "panels" && partsData ? (
                  <div className="mt-3">
                    <pre style={{ backgroundColor: "#f5f5f5", padding: "15px", borderRadius: "5px", overflow: "auto" }}>
                      {JSON.stringify(partsData, null, 2)}
                    </pre>
                  </div>
                ) : activeSubTab === "panels" ? (
                  <div className="mt-3 text-center text-muted">
                    No data available
                  </div>
                ) : null}
                {activeSubTab === "hardwares" && (
                  <div className="mt-3 table-responsive" style={{ maxHeight: "85vh", overflowY: "auto" }}>
                    {hardwaresData.length > 0 ? (
                      <Table bordered hover size="sm" className="align-middle mb-0" style={{ fontSize: "0.9rem" }}>
                        <thead className="bg-secondary text-white" style={{ position: "sticky", top: 0, zIndex: 10 }}>
                          <tr>
                            <th scope="col" className="bg-secondary text-white text-center" style={{ width: "50px" }}>
                              <span id="select-all-hardwares-checkbox" style={{ display: "inline-block" }}>
                                <input
                                  type="checkbox"
                                  checked={isAllHardwaresSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectAllHardwares(e.target.checked);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={hasOnlyCompletedHardwares}
                                  className="form-check-input"
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: hasOnlyCompletedHardwares ? "not-allowed" : "pointer",
                                  }}
                                />
                                {hasOnlyCompletedHardwares && (
                                  <UncontrolledTooltip target="select-all-hardwares-checkbox" placement="bottom">
                                    Can't move hardwares from delivered shipments.
                                  </UncontrolledTooltip>
                                )}
                              </span>
                            </th>
                            <th scope="col" className="bg-secondary text-white">Hardware</th>
                            <th scope="col" className="bg-secondary text-white text-center">Count</th>
                            <th scope="col" className="bg-secondary text-white">Room</th>
                            <th scope="col" className="bg-secondary text-white">Section</th>
                            <th scope="col" className="bg-secondary text-white">Cabinet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hardwaresData.map((hw, index) => {
                            const isCompleted = hw._isCompleted;
                            const isSelected = hw.id && selectedHardwareIds.has(hw.id);
                            const checkboxId = `hardware-checkbox-${index}-${hw.id}`;
                            return (
                              <tr
                                key={hw.id || index}
                                onClick={() => !isCompleted && handleHardwareRowSelect(hw.id)}
                                style={{
                                  backgroundColor: isSelected ? '#d0e7ff' : undefined,
                                  cursor: isCompleted ? "not-allowed" : "pointer",
                                  opacity: isCompleted ? 0.6 : 1
                                }}
                              >
                                <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                  <span id={checkboxId} style={{ display: "inline-block" }}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => !isCompleted && handleHardwareRowSelect(hw.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={isCompleted}
                                      className="form-check-input"
                                      style={{
                                        width: "18px",
                                        height: "18px",
                                        cursor: isCompleted ? "not-allowed" : "pointer"
                                      }}
                                    />
                                    {isCompleted && (
                                      <UncontrolledTooltip target={checkboxId} placement="bottom">
                                        Can't move hardwares from delivered shipments.
                                      </UncontrolledTooltip>
                                    )}
                                  </span>
                                </td>
                                <td>{hw.material_name || "-"}</td>
                                <td className="text-center">{hw.quantity || 0}</td>
                                <td>{hw.room_name || "-"}</td>
                                <td>{hw.section_name || "-"}</td>
                                <td>{hw.order_item_name || "-"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-center text-muted p-4">
                        No hardwares data available
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="my-3">
          <div className="mt-3">
            <div className="alert alert-warning">
              Please provide orderId and projectId as query parameters.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageParts;

